import React, { useState, useEffect } from 'react';
import { Clock, X, Check, XCircle } from 'lucide-react';
import './ExtensionRequestModal.css';
import { getAuth } from 'firebase/auth';
import { doc, updateDoc, getDoc, collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { ProjectIdGenerator } from '../utils/projectIdGenerator';

function ExtensionRequestModal({ isOpen, onClose, creatorData, onExtensionResponse }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  // Log when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('🔵 ExtensionRequestModal opened:', {
        hasCreatorData: !!creatorData,
        creatorId: creatorData?.id,
        projectId: creatorData?.projectId,
        campaignId: creatorData?.campaignId,
        briefId: creatorData?.briefId,
        hasGraceRequest: !!creatorData?.graceRequest,
        graceRequestStatus: creatorData?.graceRequest?.status,
        fullCreatorData: creatorData
      });
    }
  }, [isOpen, creatorData]);

  if (!isOpen || !creatorData || !creatorData.graceRequest || creatorData.graceRequest.status !== 'pending') {
    if (isOpen && !creatorData) {
      console.error('❌ ExtensionRequestModal: No creatorData provided');
    } else if (isOpen && creatorData && !creatorData.graceRequest) {
      console.error('❌ ExtensionRequestModal: No graceRequest in creatorData:', creatorData);
    } else if (isOpen && creatorData?.graceRequest?.status !== 'pending') {
      console.error('❌ ExtensionRequestModal: Grace request status is not pending:', creatorData?.graceRequest?.status);
    }
    return null;
  }

  // Validate that projectId exists
  if (!creatorData.projectId) {
    console.error('❌ ExtensionRequestModal: Missing projectId in creatorData:', creatorData);
    return (
      <div className="extension-request-modal-overlay" onClick={onClose}>
        <div className="extension-request-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="extension-request-modal-header">
            <h2>Error</h2>
            <button className="extension-request-modal-close" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
          <div className="extension-request-modal-body">
            <p>Project ID is missing. Please refresh the page and try again.</p>
            <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
              Creator ID: {creatorData.id || 'N/A'}<br/>
              Campaign ID: {creatorData.campaignId || 'N/A'}<br/>
              Brief ID: {creatorData.briefId || 'N/A'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const graceRequest = creatorData.graceRequest;
  const revisionNumber = creatorData.revisionCount || 0;

  const handleApprove = async () => {
    if (isLoading || isApproving) return;
    
    setIsLoading(true);
    setIsApproving(true);
    
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Validate required data
      if (!creatorData.projectId) {
        throw new Error('Project ID is missing. Cannot process extension request.');
      }

      if (!creatorData.campaignId || !creatorData.briefId) {
        throw new Error('Campaign or Brief ID is missing.');
      }

      const briefRef = doc(db, 'users', user.uid, 'campaigns', creatorData.campaignId, 'briefs', creatorData.briefId);
      const briefDoc = await getDoc(briefRef);
      
      if (!briefDoc.exists()) {
        console.error('❌ Brief document does not exist:', {
          path: `users/${user.uid}/campaigns/${creatorData.campaignId}/briefs/${creatorData.briefId}`
        });
        throw new Error('Brief not found');
      }

      const briefData = briefDoc.data();
      const creators = briefData.creators || [];
      
      // Find the creator entry by projectId (strict matching to avoid mismatches)
      if (!creatorData.projectId) {
        console.error('❌ CRITICAL: projectId is missing in creatorData:', creatorData);
        throw new Error('Project ID is required. Cannot process extension request without project ID.');
      }

      const normalizedProjectId = ProjectIdGenerator.normalizeProjectId(creatorData.projectId);
      console.log('🔍 Looking for creator with (approve):', {
        creatorId: creatorData.id,
        projectId: creatorData.projectId,
        normalizedProjectId,
        totalCreators: creators.length,
        creatorIds: creators.map(c => c.id),
        creatorProjectIds: creators.map(c => c.projectId || c.progress?.projectId || 'MISSING')
      });
      
      let creatorFound = false;
      
      const updatedCreators = creators.map(c => {
        const creatorProjectId = c.projectId || c.progress?.projectId || null;
        const creatorNormalizedProjectId = creatorProjectId ? ProjectIdGenerator.normalizeProjectId(creatorProjectId) : null;
        const matchesCreator = c.id === creatorData.id;
        
        // Strict matching: must match both creatorId AND projectId
        const matchesProject = normalizedProjectId && creatorNormalizedProjectId
          ? ProjectIdGenerator.areEquivalent(creatorNormalizedProjectId, normalizedProjectId)
          : false;
        
        if (matchesCreator) {
          console.log('  ✅ Creator ID matches (approve):', {
            id: c.id,
            projectId: creatorProjectId,
            normalizedProjectId: creatorNormalizedProjectId,
            targetNormalized: normalizedProjectId,
            matchesProject,
            areEquivalent: normalizedProjectId && creatorNormalizedProjectId 
              ? ProjectIdGenerator.areEquivalent(creatorNormalizedProjectId, normalizedProjectId)
              : 'N/A (one is missing)'
          });
        }
        
        if (matchesCreator && matchesProject) {
          creatorFound = true;
          console.log('  ✅ FOUND MATCHING CREATOR ENTRY (approve)!');
          const originalDeadline = new Date(c.deadline);
          const extendedDeadline = new Date(originalDeadline.getTime() + (graceRequest.requestedDays * 24 * 60 * 60 * 1000));

          return {
            ...c,
            graceRequest: {
              ...c.graceRequest,
              status: 'approved',
              respondedAt: new Date().toISOString(),
              approvedDays: graceRequest.requestedDays
            },
            extendedDeadline: extendedDeadline.toISOString()
          };
        }
        return c;
      });

      if (!creatorFound) {
        console.error('❌ Creator not found in brief for extension approval:', {
          creatorId: creatorData.id,
          projectId: creatorData.projectId,
          normalizedProjectId,
          campaignId: creatorData.campaignId,
          briefId: creatorData.briefId,
          availableCreators: creators.map(c => {
            const cProjectId = c.projectId || c.progress?.projectId || null;
            const cNormalized = cProjectId ? ProjectIdGenerator.normalizeProjectId(cProjectId) : null;
            return {
              id: c.id,
              projectId: cProjectId,
              normalizedProjectId: cNormalized,
              status: c.status,
              matchesCreator: c.id === creatorData.id,
              matchesProject: normalizedProjectId && cNormalized 
                ? ProjectIdGenerator.areEquivalent(cNormalized, normalizedProjectId)
                : false
            };
          })
        });
        throw new Error('Creator assignment not found in brief');
      }

      await updateDoc(briefRef, { creators: updatedCreators });

      // Notify creator of approval
      try {
        const notification = {
          type: 'grace_approved',
          title: 'Extension Approved',
          message: `Your ${graceRequest.requestedDays} day extension for "${creatorData.briefName}" has been approved`,
          // REQUIRED fields for notification routing and security
          recipientId: creatorData.id,
          creatorId: creatorData.id,
          projectId: creatorData.projectId || null,
          briefId: creatorData.briefId || null,
          campaignId: creatorData.campaignId || null,
          details: {
            brandUserId: user.uid,
            projectId: creatorData.projectId || null,
            campaignId: creatorData.campaignId || null,
            campaignName: creatorData.campaignName,
            briefId: creatorData.briefId || null,
            briefName: creatorData.briefName,
            approvedDays: graceRequest.requestedDays,
            approvedAt: new Date().toISOString()
          },
          read: false,
          createdAt: new Date().toISOString()
        };
        const creatorNotificationsRef = collection(db, 'creators', creatorData.id, 'notifications');
        await addDoc(creatorNotificationsRef, notification);
      } catch (notifError) {
        console.error('Error sending creator notification:', notifError);
      }

      if (onExtensionResponse) {
        onExtensionResponse('approved', graceRequest.requestedDays);
      }

      onClose();
    } catch (error) {
      console.error('Error approving extension request:', error);
      const errorMessage = error.message === 'Creator assignment not found in brief' 
        ? 'Project not found. Please refresh the page and try again.'
        : 'Failed to approve extension request. Please try again.';
      alert(errorMessage);
    } finally {
      setIsLoading(false);
      setIsApproving(false);
    }
  };

  const handleReject = async () => {
    if (isLoading || isRejecting) return;
    
    if (!window.confirm('Are you sure you want to reject this extension request?')) {
      return;
    }

    setIsLoading(true);
    setIsRejecting(true);
    
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const briefRef = doc(db, 'users', user.uid, 'campaigns', creatorData.campaignId, 'briefs', creatorData.briefId);
      const briefDoc = await getDoc(briefRef);
      
      if (!briefDoc.exists()) {
        throw new Error('Brief not found');
      }

      const briefData = briefDoc.data();
      const creators = briefData.creators || [];
      
      // Find the creator entry by projectId (strict matching to avoid mismatches)
      const normalizedProjectId = ProjectIdGenerator.normalizeProjectId(creatorData.projectId);
      let creatorFound = false;
      
      const updatedCreators = creators.map(c => {
        const creatorProjectId = c.projectId || c.progress?.projectId || null;
        const creatorNormalizedProjectId = creatorProjectId ? ProjectIdGenerator.normalizeProjectId(creatorProjectId) : null;
        const matchesCreator = c.id === creatorData.id;
        
        // Strict matching: must match both creatorId AND projectId
        const matchesProject = normalizedProjectId && creatorNormalizedProjectId
          ? ProjectIdGenerator.areEquivalent(creatorNormalizedProjectId, normalizedProjectId)
          : false;
        
        if (matchesCreator && matchesProject) {
          creatorFound = true;
          return {
            ...c,
            graceRequest: {
              ...c.graceRequest,
              status: 'denied',
              respondedAt: new Date().toISOString()
            }
          };
        }
        return c;
      });

      if (!creatorFound) {
        throw new Error('Creator assignment not found in brief');
      }

      await updateDoc(briefRef, { creators: updatedCreators });

      // Notify creator of rejection
      try {
        const notification = {
          type: 'grace_denied',
          title: 'Extension Request Denied',
          message: `Your extension request for "${creatorData.briefName}" was not approved`,
          // REQUIRED fields for notification routing and security
          recipientId: creatorData.id,
          creatorId: creatorData.id,
          projectId: creatorData.projectId || null,
          briefId: creatorData.briefId || null,
          campaignId: creatorData.campaignId || null,
          details: {
            brandUserId: user.uid,
            projectId: creatorData.projectId || null,
            campaignId: creatorData.campaignId || null,
            campaignName: creatorData.campaignName,
            briefId: creatorData.briefId || null,
            briefName: creatorData.briefName,
            deniedAt: new Date().toISOString()
          },
          read: false,
          createdAt: new Date().toISOString()
        };
        const creatorNotificationsRef = collection(db, 'creators', creatorData.id, 'notifications');
        await addDoc(creatorNotificationsRef, notification);
      } catch (notifError) {
        console.error('Error sending creator notification:', notifError);
      }

      if (onExtensionResponse) {
        onExtensionResponse('denied');
      }

      onClose();
    } catch (error) {
      console.error('❌ Error rejecting extension request:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        creatorData: {
          id: creatorData?.id,
          projectId: creatorData?.projectId,
          campaignId: creatorData?.campaignId,
          briefId: creatorData?.briefId
        }
      });
      const errorMessage = error.message === 'Creator assignment not found in brief' || error.message.includes('not found')
        ? 'Project not found. Please refresh the page and try again.'
        : error.message || 'Failed to reject extension request. Please try again.';
      alert(errorMessage);
    } finally {
      setIsLoading(false);
      setIsRejecting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  return (
    <div className="extension-request-modal-overlay" onClick={onClose}>
      <div className="extension-request-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="extension-request-modal-header">
          <div className="extension-request-header-icon">
            <Clock size={24} />
          </div>
          <div className="extension-request-header-text">
            <h2>Extension Request</h2>
            <p>{creatorData.name || creatorData.creator} - {creatorData.briefName}</p>
          </div>
          <button 
            className="extension-request-modal-close"
            onClick={onClose}
            disabled={isLoading}
          >
            <X size={20} />
          </button>
        </div>

        <div className="extension-request-modal-body">
          <div className="extension-request-info-card">
            <div className="extension-request-info-row">
              <span className="extension-request-label">Revision:</span>
              <span className="extension-request-value">
                {revisionNumber > 0 ? `Revision ${revisionNumber}` : 'Initial Submission'}
              </span>
            </div>
            
            <div className="extension-request-info-row">
              <span className="extension-request-label">Requested Extension:</span>
              <span className="extension-request-value extension-request-days">
                {graceRequest.requestedDays} {graceRequest.requestedDays === 1 ? 'day' : 'days'}
              </span>
            </div>

            {graceRequest.requestedAt && (
              <div className="extension-request-info-row">
                <span className="extension-request-label">Requested On:</span>
                <span className="extension-request-value">
                  {formatDate(graceRequest.requestedAt)}
                </span>
              </div>
            )}
          </div>

          {graceRequest.reason && (
            <div className="extension-request-reason-section">
              <h3>Reason</h3>
              <div className="extension-request-reason-text">
                {graceRequest.reason}
              </div>
            </div>
          )}
        </div>

        <div className="extension-request-modal-footer">
          <button 
            className="extension-request-btn extension-request-reject-btn"
            onClick={handleReject}
            disabled={isLoading}
          >
            <XCircle size={18} />
            {isRejecting ? 'Rejecting...' : 'Reject'}
          </button>
          <button 
            className="extension-request-btn extension-request-approve-btn"
            onClick={handleApprove}
            disabled={isLoading}
          >
            <Check size={18} />
            {isApproving ? 'Approving...' : 'Approve'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ExtensionRequestModal;

